import { Injectable } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup, Validators } from "@angular/forms";

import { DffFormControlType, DffOprindeligAktuelVaerdiFormGroup, DffReactiveFormControlValueMapper as Map } from "@dffedb/forms";
import { DffOprindeligAktuelVaerdi } from "@dffedb/util";
import { Huskeliste } from "@e-forsyning/common/huskeliste";
import {
    Maaler,
    MaalerAflaesningFormBuilder,
    MaalerAflaesningMedNoter,
    MaalerOprettelseFormBuilder,
    MaalerType
} from "@e-forsyning/common/maaler";
import { NoteBruger, NoteFormBuilder, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus } from "@e-forsyning/common/note";

import { MaalerskiftData } from "./model/maalerskift-data.model";
import { Maalerskift } from "./model/maalerskift.model";

@Injectable({ providedIn: "root" })
export class MaalerskiftFormBuilder {
    public maalerskiftDataToFormGroup(
        data: MaalerskiftData,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        omraader: NoteOmraade[],
        henvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        brugere: NoteBruger[]
    ): UntypedFormGroup {
        const result = new UntypedFormGroup({
            maalerskift: this.maalerskiftToFormGroup(
                data.maalerskift,
                maalerePaaLager,
                maalerTyper,
                huskelister,
                omraader,
                henvendelsestyper,
                noteOpgavestyringStatusser,
                brugere
            )
        });
        return result;
    }

    public maalerskiftToFormGroup(
        maalerskift: Maalerskift,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const result = new UntypedFormGroup({
            dato: new DffOprindeligAktuelVaerdiFormGroup(
                maalerskift.dato,
                { controlType: DffFormControlType.DateInput, placeholder: "Dato for målerskift" },
                Validators.required
            ),
            huskelisteId: this.huskelisteIdToFormControl(maalerskift.huskelisteId, huskelister),
            gammelMaaler: this.gammelMaalerAflaesningToFormGroup(
                maalerskift.gammelMaaler,
                maalerTyper,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            ),
            info1: new DffOprindeligAktuelVaerdiFormGroup(
                maalerskift.info1,
                { placeholder: "Info 1", controlType: DffFormControlType.TextInput },
                [Validators.maxLength(30)]
            ),
            info2: new DffOprindeligAktuelVaerdiFormGroup(
                maalerskift.info2,
                { placeholder: "Info 2", controlType: DffFormControlType.TextInput },
                [Validators.maxLength(30)]
            ),
            nyMaaler: this.nyMaalerAflaesningToFormGroup(
                maalerskift.nyMaaler,
                maalerePaaLager,
                maalerTyper,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            )
        });
        return result;
    }

    public formGroupToMaalerskiftData(formGroup: UntypedFormGroup): MaalerskiftData {
        const result: MaalerskiftData = {
            maalerskift: this.formGroupToMaalerskift(formGroup.controls["maalerskift"] as UntypedFormGroup)
        };
        return result;
    }

    public formGroupToMaalerskift(formGroup: UntypedFormGroup): Maalerskift {
        const result: Maalerskift = {
            dato: Map.toOprindeligAktuelDates(formGroup.controls["dato"]),
            gammelMaaler: this.formGroupToMaalerAflaesning(formGroup.controls["gammelMaaler"] as UntypedFormGroup),
            huskelisteId: Map.toOprindeligAktuelIntegers(formGroup.controls["huskelisteId"]),
            info1: Map.toOprindeligAktuelStrings(formGroup.controls["info1"]),
            info2: Map.toOprindeligAktuelStrings(formGroup.controls["info2"]),
            nyMaaler: this.formGroupToMaalerAflaesning(formGroup.controls["nyMaaler"] as UntypedFormGroup)
        };
        return result;
    }

    private gammelMaalerAflaesningToFormGroup(
        aflaesning: MaalerAflaesningMedNoter,
        maalerTyper: MaalerType[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const maalerType = maalerTyper.find((t) => t.id === aflaesning.maalerTypeId.aktuel) || ({ taellevaerker: [] } as MaalerType);
        const result = MaalerAflaesningFormBuilder.maalerAflaesningToFormGroup(aflaesning, maalerType, true);
        result.setControl(
            "noter",
            NoteFormBuilder.noterToFormArray(aflaesning.noter, noteOmraader, noteHenvendelsestyper, noteOpgavestyringStatusser, noteBrugere)
        );
        return result;
    }

    private nyMaalerAflaesningToFormGroup(
        aflaesning: MaalerAflaesningMedNoter,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const result = MaalerOprettelseFormBuilder.maalerAflaesningToFormGroup(aflaesning, maalerePaaLager, maalerTyper);
        result.setControl(
            "noter",
            NoteFormBuilder.noterToFormArray(aflaesning.noter, noteOmraader, noteHenvendelsestyper, noteOpgavestyringStatusser, noteBrugere)
        );
        return result;
    }

    private huskelisteIdToFormControl(huskelisteId: DffOprindeligAktuelVaerdi<number>, huskelister: Huskeliste[]): UntypedFormGroup {
        const result = new DffOprindeligAktuelVaerdiFormGroup(huskelisteId, {
            controlType: DffFormControlType.DropdownList,
            dropdownListOptions: [{}, ...huskelister],
            placeholder: "Huskeliste"
        });
        return result;
    }

    private formGroupToMaalerAflaesning(formGroup: UntypedFormGroup): MaalerAflaesningMedNoter {
        const maalerAflaesning = MaalerAflaesningFormBuilder.formGroupToMaalerAflaesning(formGroup);
        const noter = NoteFormBuilder.formArrayToNoter(formGroup.controls["noter"] as UntypedFormArray);
        const result: MaalerAflaesningMedNoter = {
            ...maalerAflaesning,
            noter
        };
        return result;
    }
}
